import {
  ROUTE_FORGOT_PASSWORD,
  ROUTE_LANDING,
  ROUTE_LOGIN,
  ROUTE_REGISTRATION,
  ROUTE_REGISTRATION_SUCCESS,
  ROUTE_ROOMS_REGISTRATION,
  SESSION_JWT_EXPIRES_AT_KEY,
  SESSION_JWT_TOKEN_KEY,
  SESSION_JWT_USER_KEY,
} from "./VMConstants";
import { VM_DOMAIN } from "./URLConstants";

/**
 * Auth endpoints that must not send Authorization (login, signup, OTP, reset).
 * Other listed public APIs still work without a JWT; we skip the header there too
 * so unauthenticated pages never attach a leftover token.
 */
export const PUBLIC_AUTH_PATHS = [
  "/v1/users/login",
  "/v1/users/registration",
  "/v1/users/verifyOTP",
  "/v1/users/resendOTP",
  "/v1/users/customerforgotpassword",
  "/v1/users/updatePassword",
  "/v1/users/submitContact",
  "/v1/users/countryListByName",
  "/v1/users/countryList",
  "/v1/users/stateList",
  "/v1/users/cityList",
  "/v1/payment/subscriptionPlans",
  "/v1/feedback/submitFeedbackForm",
  "/v1/feedback/submitFeedback",
  "/v1/feedback/getFeedbackDetails",
  "/v1/visitor/submitVisitForm",
];

const AUTH_PAGE_PATHS = [
  `/${ROUTE_LOGIN}`,
  `/${ROUTE_REGISTRATION}`,
  `/${ROUTE_FORGOT_PASSWORD}`,
  `/${ROUTE_LANDING}`,
  `/${ROUTE_REGISTRATION_SUCCESS}`,
  `/${ROUTE_ROOMS_REGISTRATION}`,
];

const getStorage = (type) => {
  if (typeof window === "undefined") return null;
  try {
    return type === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
};

export const getRequestPathname = (url = "") => {
  if (!url || typeof url !== "string") return "";
  try {
    if (/^https?:\/\//i.test(url)) {
      return new URL(url).pathname.replace(/\/+$/, "") || "/";
    }
  } catch {
    /* fall through */
  }
  const path = url.split("?")[0].split("#")[0];
  return path.replace(/\/+$/, "") || path;
};

export const isPublicAuthPath = (url = "") => {
  const path = getRequestPathname(url);
  if (!path) return false;
  return PUBLIC_AUTH_PATHS.some((publicPath) => path === publicPath);
};

export const isVenueManagerApiUrl = (url = "") => {
  if (!url || typeof url !== "string") return false;
  const domain = String(VM_DOMAIN || "").replace(/\/+$/, "");
  if (domain && url.startsWith(domain)) return true;
  if (/^https?:\/\//i.test(url)) return false;
  const path = getRequestPathname(url);
  return path.startsWith("/v1/");
};

export const getAuthToken = () =>
  getStorage("session")?.getItem(SESSION_JWT_TOKEN_KEY) ||
  getStorage("local")?.getItem(SESSION_JWT_TOKEN_KEY) ||
  "";

export const getAuthUser = () => {
  const raw = getStorage("session")?.getItem(SESSION_JWT_USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const setAuthToken = (token) => {
  if (!token) return;
  getStorage("session")?.setItem(SESSION_JWT_TOKEN_KEY, String(token));
};

export const clearAuthToken = () => {
  ["session", "local"].forEach((type) => {
    const storage = getStorage(type);
    storage?.removeItem(SESSION_JWT_TOKEN_KEY);
    storage?.removeItem(SESSION_JWT_USER_KEY);
    storage?.removeItem(SESSION_JWT_EXPIRES_AT_KEY);
  });
};

/**
 * Persist JWT (and optional user / expiry) from login, verifyOTP, or pending
 * venue / payment responses.
 */
export const saveAuthFromResponse = (data) => {
  if (!data || typeof data !== "object" || !data.token) return false;

  const session = getStorage("session");
  setAuthToken(data.token);

  if (data.user && typeof data.user === "object") {
    session?.setItem(SESSION_JWT_USER_KEY, JSON.stringify(data.user));
  }

  if (data.expires_in != null && data.expires_in !== "") {
    const expiresAt = Date.now() + Number(data.expires_in) * 1000;
    if (!Number.isNaN(expiresAt)) {
      session?.setItem(SESSION_JWT_EXPIRES_AT_KEY, String(expiresAt));
    }
  }

  return true;
};

export const shouldAttachAuthHeader = (url) => {
  if (!url || isPublicAuthPath(url)) return false;
  return isVenueManagerApiUrl(url);
};

export const buildAuthHeaders = (url, existing = {}) => {
  const headers = { Accept: "application/json", ...existing };
  const token = getAuthToken();
  if (token && shouldAttachAuthHeader(url)) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

/** For `<img src>` file URLs that cannot send headers. */
export const withFileAuthToken = (url) => {
  if (!url || typeof url !== "string") return url;
  const token = getAuthToken();
  if (!token || !isVenueManagerApiUrl(url)) return url;
  if (/[?&]token=/.test(url)) return url;
  const join = url.includes("?") ? "&" : "?";
  return `${url}${join}token=${encodeURIComponent(token)}`;
};

export const isAuthPagePath = (pathname = "") => {
  const path = pathname.split("?")[0].replace(/\/+$/, "") || "/";
  return AUTH_PAGE_PATHS.some(
    (page) => path === page || path.startsWith(`${page}/`)
  );
};

export const isUnauthorizedPayload = (status, data) =>
  Number(status) === 401 || Number(data?.status_code) === 401;
